import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Bell, LogOut, Menu } from "lucide-react";
import { useTranslation } from "react-i18next";

import "./PlatformAppBar.css";
import logoImg from "../../assets/logo2.png";
import LanguageSwitcher from "../LanguageSwitcher";
import NotificationBell from "../notifications/NotificationBell";

export default function PlatformAppBar({
  actorType,
  actorLabel,
  fullName = "User",
  initials = "U",
  notificationCount = 0,
  homePath = "/",
  onOpenMobileMenu,
  avatarImageUrl = "",
  avatarAlt = "",
  avatarFit = "cover",
  notificationsEnabled = true,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const { logout } = useAuth0();

  const { t } = useTranslation("common");

  const [profileMenuState, setProfileMenuState] = React.useState("closed");

  const [notificationsOpen, setNotificationsOpen] = React.useState(false);

  const [actualNotificationCount, setActualNotificationCount] =
    React.useState(notificationCount);

  const menuRef = React.useRef(null);

  const PROFILE_MENU_AUTO_CLOSE_MS = 4000;
  const PROFILE_MENU_ANIMATION_MS = 180;

  const closeProfileMenu = React.useCallback(() => {
    setProfileMenuState((currentState) => {
      if (currentState !== "open") {
        return currentState;
      }

      return "closing";
    });
  }, []);

  React.useEffect(() => {
    if (profileMenuState !== "closing") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setProfileMenuState("closed");
    }, PROFILE_MENU_ANIMATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [profileMenuState]);

  React.useEffect(() => {
    if (profileMenuState !== "open" || notificationsOpen) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      closeProfileMenu();
    }, PROFILE_MENU_AUTO_CLOSE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [profileMenuState, notificationsOpen, closeProfileMenu]);

  React.useEffect(() => {
    closeProfileMenu();
  }, [location.key, closeProfileMenu]);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (notificationsOpen) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeProfileMenu();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationsOpen, closeProfileMenu]);

  /* =====================================================
     ACTOR LABEL
     ===================================================== */

  const actorLabelMap = {
    HOMEOWNER: t("actorLabels.HOMEOWNER"),
    LENDER: t("actorLabels.LENDER"),
    OSS: t("actorLabels.OSS"),
    INVESTOR: t("actorLabels.INVESTOR"),
    PLATFORM_ADMIN: t("actorLabels.PLATFORM_ADMIN"),
  };

  const resolvedActorLabel = actorLabel || actorLabelMap[actorType] || "";

  const resolvedAvatarAlt = avatarAlt || fullName || t("appBar.profileMenu");

  /* =====================================================
     LOGOUT
     ===================================================== */

  function handleLogout() {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error("Failed to clear browser storage:", error);
    }

    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/`,
      },
    });
  }

  /* =====================================================
     AVATAR
     ===================================================== */

  function renderAvatar(size = "default") {
    const avatarClass =
      size === "large"
        ? "platform-appbar__avatar platform-appbar__avatar--large"
        : "platform-appbar__avatar";

    if (avatarImageUrl) {
      return (
        <span className={`${avatarClass} platform-appbar__avatar--image-wrap`}>
          <img
            src={avatarImageUrl}
            alt={resolvedAvatarAlt}
            className="platform-appbar__avatar-image"
            style={{
              objectFit: avatarFit,
            }}
          />
        </span>
      );
    }

    return <span className={avatarClass}>{initials}</span>;
  }

  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <div className="platform-appbar">
      <div className="platform-appbar__inner">
        {/* LEFT */}

        <div className="platform-appbar__left">
          <button
            type="button"
            className="platform-appbar__menu-btn"
            onClick={onOpenMobileMenu}
            aria-label={t("appBar.openMenu")}
          >
            <Menu size={22} />
          </button>

          <button
            type="button"
            className="platform-appbar__brand"
            onClick={() => navigate(homePath)}
          >
            <img
              src={logoImg}
              alt={t("appBar.logoAlt")}
              className="platform-appbar__logo"
            />
          </button>
        </div>

        {/* RIGHT */}

        <div className="platform-appbar__right">
          {resolvedActorLabel ? (
            <span className="platform-appbar__role">{resolvedActorLabel}</span>
          ) : null}

          <LanguageSwitcher />

          <div className="platform-appbar__avatar-wrap" ref={menuRef}>
            <button
              type="button"
              className="platform-appbar__avatar-trigger"
              onClick={() => {
                if (notificationsOpen) {
                  return;
                }

                setProfileMenuState((currentState) =>
                  currentState === "open" ? "closing" : "open",
                );
              }}
              aria-label={t("appBar.profileMenu")}
              aria-expanded={profileMenuState === "open"}
              aria-haspopup="menu"
            >
              {renderAvatar("default")}

              {actualNotificationCount > 0 ? (
                <span className="platform-appbar__notif-badge">
                  {actualNotificationCount > 9 ? "9+" : actualNotificationCount}
                </span>
              ) : null}
            </button>

            {profileMenuState !== "closed" ? (
              <div
                className={`platform-appbar__menu ${
                  profileMenuState === "closing"
                    ? "platform-appbar__menu--closing"
                    : ""
                }`}
                role="menu"
              >
                <div className="platform-appbar__profile">
                  {renderAvatar("large")}

                  <span>{fullName}</span>
                </div>

                <div className="platform-appbar__divider" />

                <button
                  type="button"
                  className="platform-appbar__menu-item platform-appbar__menu-item--notifications"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    closeProfileMenu();
                    setNotificationsOpen(true);
                  }}
                >
                  <Bell size={18} />

                  <span>{t("appBar.notifications")}</span>

                  <span className="platform-notifications__menu-count">
                    {actualNotificationCount > 9
                      ? "9+"
                      : actualNotificationCount}
                  </span>
                </button>

                <button
                  type="button"
                  className="platform-appbar__menu-item"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />

                  <span>{t("appBar.logout")}</span>
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <NotificationBell
        variant="controller"
        open={notificationsOpen}
        onOpenChange={setNotificationsOpen}
        onUnreadCountChange={setActualNotificationCount}
        enabled={notificationsEnabled}
      />
    </div>
  );
}
